import { useState } from 'react'
import { motion } from 'framer-motion'
import { RiEdit2Line, RiDeleteBin6Line, RiEyeLine, RiUserAddLine, RiUserLine } from 'react-icons/ri'
import { FaRegBuilding, FaUsers, FaCrown } from 'react-icons/fa'
import { Button } from '../../../components/Button'
import { ConfirmModal, Loading } from '../../../components'
import type { Setor } from '../../../models'
import { isEquipe } from '../../../models'
import { UserAvatar } from '../../../components/UserAvatar'
import type { ProcessPermissions } from '../hooks/usePermissions'
import { useSortableTable } from '../hooks/useSortableTable'
import { SortableHeader } from './SortableHeader'

interface SetoresTableProps {
  setores: Setor[]
  permissions: ProcessPermissions
  isLoading?: boolean
  showEquipes?: boolean // Se true, mostra apenas equipes; se false, apenas setores
  onEdit: (setor: Setor) => void
  onDelete: (setor: Setor) => void
  onView?: (setor: Setor) => void
  onManageUsers?: (setor: Setor) => void
  searchQuery?: string
}


export const SetoresTable = ({ 
  setores, 
  permissions,
  isLoading = false, 
  showEquipes = false,
  onEdit, 
  onDelete, 
  onView,
  onManageUsers,
  searchQuery = ''
}: SetoresTableProps) => {
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; setor: Setor | null }>({
    isOpen: false,
    setor: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Filtrar apenas por tipo (setor/equipe) - a busca já vem do servidor
  const filteredSetores = setores.filter(setor => {
    const setorIsEquipe = isEquipe(setor)
    return showEquipes === setorIsEquipe
  })

  // Hook de ordenação
  const { sortedData, sortConfig, requestSort } = useSortableTable(filteredSetores)


  const entityType = showEquipes ? 'equipes' : 'setores'
  const entityTypeSingular = showEquipes ? 'equipe' : 'setor'

  const handleDeleteClick = (setor: Setor) => {
    setDeleteModal({ isOpen: true, setor })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.setor) return
    
    try {
      setIsDeleting(true)
      await onDelete(deleteModal.setor)
      setDeleteModal({ isOpen: false, setor: null })
    } catch (error) {
      console.error(`Erro ao excluir ${entityTypeSingular}:`, error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, setor: null })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-8 text-center space-y-4">
          <Loading message={`Carregando ${entityType}...`} />
          <p className="text-gray-600 dark:text-gray-400">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (sortedData.length === 0 && !isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-12 text-center">
          <div className="inline-flex p-4 rounded-full bg-gray-100 dark:bg-neutral-700 mb-4">
            {showEquipes ? (
              <FaUsers className="w-8 h-8 text-gray-400" />
            ) : (
              <FaRegBuilding className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery 
              ? (showEquipes ? 'Nenhuma equipe encontrada' : 'Nenhum setor encontrado')
              : (showEquipes ? 'Nenhuma equipe cadastrada' : 'Nenhum setor cadastrado')
            }
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery 
              ? `Nenhum ${entityTypeSingular} corresponde à busca "${searchQuery}".`
              : `Não há ${entityType} cadastrados no momento.`
            }
          </p>
          {!searchQuery && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Use o botão &quot;Criar&quot; para adicionar {showEquipes ? 'a primeira equipe' : 'o primeiro setor'}.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden flex flex-col h-[calc(100vh-230px)]">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full divide-y divide-gray-200 dark:divide-neutral-700 table-fixed" style={{ minWidth: '950px' }}>
            <thead className="bg-gray-50 dark:bg-neutral-700 sticky top-0 z-10">
              <tr>
                <SortableHeader
                  label={showEquipes ? 'Equipe' : 'Setor'}
                  sortKey="nome_setor"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[250px] max-w-[250px]"
                />
                <SortableHeader
                  label="Chefe"
                  sortKey="chefe.name"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[220px] max-w-[220px]"
                />
                <th className="w-[200px] max-w-[200px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Membros
                </th>
                <SortableHeader
                  label="Criado em"
                  sortKey="created_at"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[140px] max-w-[140px]"
                />
                <th className="w-[140px] max-w-[140px] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
              {sortedData.map((setor, index) => (
                <motion.tr
                  key={setor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
                >
                  <td className="px-4 py-4 w-[250px] max-w-[250px]">
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        showEquipes ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {showEquipes ? (
                          <FaUsers className={`w-5 h-5 ${showEquipes ? 'text-purple-600' : 'text-blue-600'}`} />
                        ) : (
                          <FaRegBuilding className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 overflow-hidden" title={setor.nome_setor}>
                          <span className="truncate">{setor.nome_setor}</span>
                          {/* {setor.equipe && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
                              Equipe
                            </span>
                          )} */}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">ID: {setor.id}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 w-[220px] max-w-[220px]">
                    {setor.chefe ? (
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <UserAvatar user={setor.chefe} size="sm" className="mr-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1 overflow-hidden" title={setor.chefe.name}>
                            <FaCrown className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                            <span className="truncate">{setor.chefe.name}</span>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate" title={setor.chefe.cargo_usuario}>
                            {setor.chefe.cargo_usuario}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400 italic truncate">Sem chefe definido</span>
                    )}
                  </td>
                  
                  <td className="px-4 py-4 w-[200px] max-w-[200px]">
                    <div className="flex items-center overflow-hidden">
                      <RiUserLine className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {(setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)?.length || 0} {((setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)?.length || 0) === 1 ? 'membro' : 'membros'}
                      </span>
                    </div>
                    {(setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios) && (setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)!.length > 0 && (
                      <div className="flex -space-x-1 mt-2 overflow-hidden">
                        {(setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)!.slice(0, 3).map((usuario) => (
                          <UserAvatar 
                            key={usuario.id} 
                            user={usuario} 
                            size="xs" 
                            className="border-2 border-white" 
                          />
                        ))}
                        {(setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)!.length > 3 && (
                          <div className="w-6 h-6 bg-gray-100 border-2 border-white rounded-full flex items-center justify-center">
                            <span className="text-xs text-gray-600 font-medium">
                              +{(setor.usuarios_ativos || setor.usuariosAtivos || setor.usuarios)!.length - 3}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-4 py-4 w-[140px] max-w-[140px]">
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {setor.created_at ? new Date(setor.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center w-[140px] max-w-[140px]">
                    <div className="flex items-center justify-center space-x-2">
                      {onView && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(setor)}
                          className="text-gray-600 hover:text-blue-600"
                          title={`Visualizar ${entityTypeSingular}`}
                        >
                          <RiEyeLine className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {onManageUsers && permissions.canManageUsers && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onManageUsers(setor)}
                          className="text-gray-600 hover:text-green-600"
                          title="Gerenciar usuários"
                        >
                          <RiUserAddLine className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {((showEquipes && permissions.canEditEquipes) || (!showEquipes && permissions.canEditSetores)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(setor)}
                          className="text-gray-600 hover:text-blue-600"
                          title={`Editar ${entityTypeSingular}`}
                        >
                          <RiEdit2Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {((showEquipes && permissions.canDeleteEquipes) || (!showEquipes && permissions.canDeleteSetores)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(setor)}
                          className="text-gray-600 hover:text-red-600"
                          title={`Excluir ${entityTypeSingular}`}
                        >
                          <RiDeleteBin6Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Mostrar se não há ações disponíveis */}
                      {!onView && 
                       (!permissions.canManageUsers || !onManageUsers) &&
                       !((showEquipes && permissions.canEditEquipes) || (!showEquipes && permissions.canEditSetores)) &&
                       !((showEquipes && permissions.canDeleteEquipes) || (!showEquipes && permissions.canDeleteSetores)) && (
                        <span className="text-xs text-gray-400 italic">Somente leitura</span>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        type='danger'
        title={`Excluir ${entityTypeSingular}`}
        message={`Tem certeza que deseja excluir o ${entityTypeSingular} ${deleteModal.setor?.nome_setor}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />
    </>
  )
}
